import { useCallback, useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { fmtDMY } from "@/lib/utils";
import { DateField } from "@/components/DateField";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Phone, Upload, Download, LayoutGrid, Rows3, Search } from "lucide-react";
import { RolesDialog } from "@/components/RolesDialog";

const empty = { name: "", phone: "", role: "", status: "available", unavailable_days: [], leave_start: null, leave_end: null };
const todayStr = () => new Date().toLocaleDateString("en-CA", { timeZone: "Australia/Brisbane" });
const fmtDate = fmtDMY;
const STATUS_STYLE = {
  available: ["Available", "border-[hsl(var(--success))] text-[hsl(var(--success))]"],
  disabled: ["Disabled", "border-destructive text-destructive"],
  unavailable: ["On leave", "border-amber-500 text-amber-600"],
};

function leaveText(s) {
  if (s.status !== "unavailable" || !s.leave_start) return "";
  return s.leave_start > todayStr() ? ` from ${fmtDate(s.leave_start)}–${fmtDate(s.leave_end)}` : ` until ${fmtDate(s.leave_end)}`;
}

function StatusBadge({ s, className = "" }) {
  const [label, cls] = STATUS_STYLE[s.status] || STATUS_STYLE.disabled;
  return (
    <Badge variant="outline" data-testid={`staff-status-badge-${s.id}`} className={`${cls} rounded-full whitespace-nowrap ${className}`}>
      {label}{leaveText(s)}
    </Badge>
  );
}

function RoleCell({ s }) {
  return s.role ? <span>{s.role}</span> : (
    <Badge variant="outline" data-testid={`role-needed-${s.id}`} className="rounded-full border-destructive text-destructive whitespace-nowrap">Role needed</Badge>
  );
}
const DAY_LABELS = [
  ["Monday", "Mon"], ["Tuesday", "Tue"], ["Wednesday", "Wed"], ["Thursday", "Thu"],
  ["Friday", "Fri"], ["Saturday", "Sat"], ["Sunday", "Sun"],
];
const FILTERS = [
  { id: "available", label: "Available" },
  { id: "unavailable", label: "On leave" },
  { id: "role_needed", label: "Role needed" },
  { id: "disabled", label: "Disabled" },
  { id: "all", label: "All" },
];

export default function StaffPage() {
  const [staff, setStaff] = useState([]);
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [leaving, setLeaving] = useState(null);
  const [filter, setFilter] = useState("available");
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [mobileView, setMobileView] = useState(() => localStorage.getItem("staffMobileView") || "cards");
  const fileRef = useRef(null);

  useEffect(() => { localStorage.setItem("staffMobileView", mobileView); }, [mobileView]);

  const load = useCallback(() => Promise.all([api.get("/staff"), api.get("/roles")]).then(([s, r]) => { setStaff(s.data); setRoles(r.data); }), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (roleFilter !== "all" && roles.length && !roles.some((r) => r.name === roleFilter)) setRoleFilter("all");
  }, [roles, roleFilter]);

  const openNew = () => { setForm(empty); setEditing(null); setOpen(true); };
  const openEdit = (s) => { setForm({ name: s.name, phone: s.phone, role: s.role || "", status: s.status, unavailable_days: s.unavailable_days || [], leave_start: s.leave_start || null, leave_end: s.leave_end || null }); setEditing(s.id); setOpen(true); };

  const onStatusChange = (v) => {
    if (v === "left") { setLeaving({ id: editing, name: form.name }); return; }
    setForm(v === "unavailable" ? { ...form, status: v } : { ...form, status: v, leave_start: null, leave_end: null });
  };

  const toggleDay = (day) => {
    setForm((f) => ({
      ...f,
      unavailable_days: f.unavailable_days.includes(day)
        ? f.unavailable_days.filter((d) => d !== day)
        : [...f.unavailable_days, day],
    }));
  };

  const counts = {
    available: staff.filter((s) => s.status === "available").length,
    unavailable: staff.filter((s) => s.status === "unavailable").length,
    role_needed: staff.filter((s) => !s.role).length,
    disabled: staff.filter((s) => s.status === "disabled").length,
    all: staff.length,
  };
  const byTab = filter === "all" ? staff : filter === "role_needed" ? staff.filter((s) => !s.role) : staff.filter((s) => s.status === filter);
  const q = search.trim().toLowerCase();
  const phoneKey = (p) => p.replace(/\D/g, "").replace(/^(61|0)/, "");
  const qPhone = phoneKey(q);
  const visible = byTab.filter((s) => (!q || s.name.toLowerCase().includes(q) || (qPhone && phoneKey(s.phone).includes(qPhone)))
    && (roleFilter === "all" || (s.role || "") === roleFilter));

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/staff/import", fd);
      toast.success(`Imported ${data.imported} staff${data.skipped ? `, skipped ${data.skipped}` : ""}`);
      if (data.errors?.length) toast.warning(data.errors.join(" • "), { duration: 12000 });
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get("/staff/template", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "staff_template.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Couldn't download the template"); }
  };

  const save = async () => {
    if (!form.name.trim() || !form.phone.trim()) { toast.error("Name and phone are required"); return; }
    if (!form.role) { toast.error("Pick a role"); return; }
    if (form.status === "unavailable" && (!form.leave_start || !form.leave_end)) { toast.error("Pick the leave start and end dates"); return; }
    try {
      if (editing) await api.put(`/staff/${editing}`, form);
      else await api.post("/staff", form);
      toast.success(editing ? "Staff updated" : "Staff added");
      setOpen(false);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const toggle = async (s) => {
    await api.patch(`/staff/${s.id}/status`);
    load();
  };

  const confirmLeft = async () => {
    try {
      await api.delete(`/staff/${leaving.id}`);
      toast.success(`${leaving.name}'s details have been deleted`);
      setLeaving(null);
      setOpen(false);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const remove = (s) => setLeaving({ id: s.id, name: s.name });

  return (
    <div className="animate-fade-up">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between mb-8">
        <div>
          <p className="uppercase text-xs tracking-[0.3em] text-muted-foreground mb-2">Crew</p>
          <h1 className="font-head text-4xl sm:text-5xl font-800 tracking-tight leading-none">Staff</h1>
          <p className="text-muted-foreground mt-3">Names, mobile numbers and availability.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={onImport} className="hidden" data-testid="staff-import-input" />
          <RolesDialog roles={roles} onChanged={load} />
          <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-button" className="gap-2">
            <Download className="h-4 w-4" /> Template
          </Button>
          <Button variant="secondary" onClick={() => fileRef.current?.click()} data-testid="import-staff-button" className="gap-2">
            <Upload className="h-4 w-4" /> Import Excel
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNew} data-testid="add-staff-button" className="gap-2 transition-transform duration-200 hover:-translate-y-0.5">
                <Plus className="h-4 w-4" /> Add staff
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="staff-dialog">
              <DialogHeader><DialogTitle className="font-head">{editing ? "Edit staff" : "Add staff"}</DialogTitle>
              <DialogDescription>Enter the crew member's contact details and availability.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Full name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-name-input" className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Mobile (04… or +61…)</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="staff-phone-input" className="mt-1.5" placeholder="0412 345 678" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Role</Label>
                <Select value={form.role || undefined} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="staff-role-select" className="mt-1.5"><SelectValue placeholder="Pick a role" /></SelectTrigger>
                  <SelectContent>
                    {roles.map((r) => <SelectItem key={r.id} value={r.name} data-testid={`role-option-${r.name.toLowerCase().replace(/\s+/g, "-")}`}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Status</Label>
                <Select value={form.status} onValueChange={onStatusChange}>
                  <SelectTrigger data-testid="staff-status-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="available" data-testid="status-option-available">Available</SelectItem>
                    <SelectItem value="unavailable" data-testid="status-option-unavailable">Temporary leave (start & end dates)</SelectItem>
                    <SelectItem value="disabled" data-testid="status-option-disabled">Disabled</SelectItem>
                    {editing ? <SelectItem value="left" data-testid="status-option-left" className="text-destructive">Left business</SelectItem> : null}
                  </SelectContent>
                </Select>
              </div>
              {form.status === "unavailable" ? (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Leave starts</Label>
                    <DateField value={form.leave_start || ""} onChange={(v) => setForm({ ...form, leave_start: v })} testid="staff-leave-start-input" className="mt-1.5" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Leave ends</Label>
                    <DateField min={form.leave_start || todayStr()} value={form.leave_end || ""} onChange={(v) => setForm({ ...form, leave_end: v })} testid="staff-leave-end-input" className="mt-1.5" />
                  </div>
                </div>
              ) : null}
              <p className="text-xs text-muted-foreground -mt-2">Staff on leave get no alerts from the start date to the end date, then switch back to Available automatically.</p>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Available days</Label>
                <p className="text-xs text-muted-foreground mt-1 mb-2">Tap to disable days this person can't work. Everyone is available all days by default.</p>
                <div className="flex flex-wrap gap-2" data-testid="staff-days-editor">
                  {DAY_LABELS.map(([day, short]) => {
                    const off = form.unavailable_days.includes(day);
                    return (
                      <button
                        key={day}
                        type="button"
                        onClick={() => toggleDay(day)}
                        data-testid={`staff-day-${short}`}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors duration-200 ${
                          off
                            ? "bg-muted text-muted-foreground line-through"
                            : "bg-[hsl(var(--success))] text-white"
                        }`}
                      >
                        {short}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} data-testid="save-staff-button">{editing ? "Save" : "Add staff"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <div className="relative sm:w-72">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or mobile" data-testid="staff-search-input" className="pl-9" />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger data-testid="staff-role-filter" className="sm:w-60"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="role-filter-all">All roles</SelectItem>
            {roles.map((r) => <SelectItem key={r.id} value={r.name} data-testid={`role-filter-${r.name.toLowerCase().replace(/\s+/g, "-")}`}>{r.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {(search || roleFilter !== "all") && (
          <Button variant="ghost" onClick={() => { setSearch(""); setRoleFilter("all"); }} data-testid="clear-staff-filters">Clear</Button>
        )}
      </div>

      <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-1" data-testid="staff-filters">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            data-testid={`filter-${f.id}`}
            className={`shrink-0 whitespace-nowrap px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors duration-200 ${
              filter === f.id
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:text-foreground"
            }`}
          >
            {f.label} <span className="opacity-70">({counts[f.id]})</span>
          </button>
        ))}
      </div>

      <Card className="overflow-hidden hidden sm:block">
        <div className="overflow-x-auto">
        <Table data-testid="staff-table" className="min-w-[680px]">
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead>Name</TableHead>
              <TableHead>Mobile</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-10">
                {staff.length === 0 ? "No staff yet. Add or import your crew." : "No staff match these filters."}
              </TableCell></TableRow>
            )}
            {visible.map((s) => (
              <TableRow key={s.id} data-testid={`staff-row-${s.id}`} className="odd:bg-muted/20">
                <TableCell className="font-medium">
                  {s.name}
                  {s.unavailable_days?.length ? (
                    <p className="text-xs font-normal text-muted-foreground" data-testid={`staff-offdays-${s.id}`}>
                      Off: {s.unavailable_days.map((d) => d.slice(0, 3)).join(", ")}
                    </p>
                  ) : null}
                </TableCell>
                <TableCell className="text-muted-foreground"><span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" />{s.phone}</span></TableCell>
                <TableCell className="text-muted-foreground"><RoleCell s={s} /></TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Switch checked={s.status === "available"} onCheckedChange={() => toggle(s)} data-testid={`staff-toggle-${s.id}`} />
                    <StatusBadge s={s} />
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(s)} data-testid={`edit-staff-${s.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(s)} data-testid={`delete-staff-${s.id}`} className="text-destructive hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </div>
      </Card>

      {/* Mobile view */}
      <div className="sm:hidden">
        <div className="flex items-center justify-end mb-3">
          <div className="inline-flex rounded-full bg-muted p-1" data-testid="staff-view-toggle">
            <button
              onClick={() => setMobileView("cards")}
              data-testid="staff-view-cards"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors duration-200 ${mobileView === "cards" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"}`}
            >
              <LayoutGrid className="h-3.5 w-3.5" /> Cards
            </button>
            <button
              onClick={() => setMobileView("compact")}
              data-testid="staff-view-compact"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors duration-200 ${mobileView === "compact" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"}`}
            >
              <Rows3 className="h-3.5 w-3.5" /> Compact
            </button>
          </div>
        </div>

        {visible.length === 0 && (
          <Card className="p-8 text-center text-muted-foreground">
            {staff.length === 0 ? "No staff yet. Add or import your crew." : "No staff match these filters."}
          </Card>
        )}

        {mobileView === "cards" ? (
          <div className="space-y-3" data-testid="staff-cards">
            {visible.map((s) => (
              <Card key={s.id} data-testid={`staff-card-${s.id}`} className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium truncate">{s.name}</p>
                    <p className="text-sm text-muted-foreground inline-flex items-center gap-1.5 mt-0.5">
                      <Phone className="h-3.5 w-3.5 shrink-0" />{s.phone}
                    </p>
                    <div className="text-xs text-muted-foreground mt-1"><RoleCell s={s} /></div>
                    {s.unavailable_days?.length ? (
                      <p className="text-xs text-muted-foreground mt-1" data-testid={`staff-offdays-m-${s.id}`}>
                        Off: {s.unavailable_days.map((d) => d.slice(0, 3)).join(", ")}
                      </p>
                    ) : null}
                  </div>
                  <StatusBadge s={s} className="shrink-0" />
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                  <div className="flex items-center gap-2">
                    <Switch checked={s.status === "available"} onCheckedChange={() => toggle(s)} data-testid={`staff-toggle-m-${s.id}`} />
                    <span className="text-xs text-muted-foreground">Available</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(s)} data-testid={`edit-staff-m-${s.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(s)} data-testid={`delete-staff-m-${s.id}`} className="text-destructive hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : visible.length > 0 && (
          <Card className="overflow-hidden" data-testid="staff-compact">
            {visible.map((s) => (
              <div
                key={s.id}
                data-testid={`staff-compact-row-${s.id}`}
                className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-border last:border-0 odd:bg-muted/20"
              >
                <span className="font-medium text-sm truncate min-w-0" onClick={() => openEdit(s)}>{s.name}</span>
                <Switch
                  checked={s.status === "available"}
                  onCheckedChange={() => toggle(s)}
                  data-testid={`staff-compact-toggle-${s.id}`}
                  className="shrink-0"
                />
              </div>
            ))}
          </Card>
        )}
      </div>

      <AlertDialog open={!!leaving} onOpenChange={(v) => { if (!v) setLeaving(null); }}>
        <AlertDialogContent data-testid="left-business-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Has {leaving?.name} left the business?</AlertDialogTitle>
            <AlertDialogDescription>Their details will be deleted permanently. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="left-business-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmLeft} data-testid="left-business-confirm" className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete details</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
